import { useState } from 'react';
import { BULK_ORDER_STATUSES, type BulkOrderStatus } from '@miraki/contracts';
import { describeError } from '../../api/errors';
import { useBulkOrderStatus, type BulkStatusResult } from '../../api/hooks';
import type { OrderSummary } from '../../api/types';
import { useSession } from '../../auth/session';
import { Alert, Button, Field, Modal } from '../../ui';

const LABEL: Record<BulkOrderStatus, string> = {
  UNDER_REVIEW: 'Under review', APPROVED: 'Approved', IN_MANUFACTURING: 'In manufacturing',
  READY: 'Ready', REJECTED: 'Rejected', CANCELLED: 'Cancelled',
};
/** The permission each target needs: the same one the order page asks for the matching action. */
const NEEDS: Record<BulkOrderStatus, string> = {
  UNDER_REVIEW: 'order.approve', APPROVED: 'order.approve', IN_MANUFACTURING: 'order.approve',
  READY: 'order.approve', REJECTED: 'order.reject', CANCELLED: 'order.cancel',
};
const NEEDS_REASON: ReadonlySet<BulkOrderStatus> = new Set<BulkOrderStatus>(['REJECTED', 'CANCELLED']);

/**
 * A-14 — change the status of the orders ticked in the queue. The server checks every order against the
 * lifecycle on its own; this shows what it did: the orders that moved, and for each that did not, why.
 */
export function BulkStatus({ orders, onDone }: { orders: OrderSummary[]; onDone: (movedIds: string[]) => void }) {
  const { me } = useSession();
  const run = useBulkOrderStatus();
  const [target, setTarget] = useState<BulkOrderStatus | ''>('');
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<BulkStatusResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const allowed = BULK_ORDER_STATUSES.filter((s) => me?.kind === 'internal' && (me.isSuperAdmin || me.permissions.includes(NEEDS[s])));
  if (allowed.length === 0) return null;

  const needsReason = target !== '' && NEEDS_REASON.has(target);
  const ready = target !== '' && (!needsReason || reason.trim().length >= 3);

  async function apply() {
    if (target === '' || run.isPending) return;
    setError(null);
    try {
      const r = await run.mutateAsync({ orderIds: orders.map((o) => o.id), status: target, ...(needsReason ? { reason: reason.trim() } : {}) });
      setResult(r);
      setConfirming(false);
      setReason('');
      onDone(r.succeeded.map((x) => x.id));
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <>
      <div className="mk-bulkbar-group">
        <span className="mk-bulkbar-label">Set status</span>
        <select value={target} aria-label="New status" onChange={(e) => setTarget(e.target.value as BulkOrderStatus | '')}>
          <option value="">Choose a status…</option>
          {allowed.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
        </select>
        <Button size="sm" disabled={target === '' || run.isPending} onClick={() => { setError(null); setConfirming(true); }}>
          Apply
        </Button>
      </div>

      {confirming && target !== '' ? (
        <Modal
          title="Update order status"
          onClose={() => { if (!run.isPending) setConfirming(false); }}
          footer={(
            <>
              <Button tone="ghost" disabled={run.isPending} onClick={() => setConfirming(false)}>Cancel</Button>
              <Button disabled={!ready || run.isPending} onClick={() => void apply()}>
                {run.isPending ? 'Updating…' : `Update ${orders.length} ${orders.length === 1 ? 'order' : 'orders'}`}
              </Button>
            </>
          )}
        >
          <p>
            Move <b>{orders.length}</b> selected {orders.length === 1 ? 'order' : 'orders'} to <b>{LABEL[target]}</b>?
            Each order is checked on its own; one that cannot make the move is left as it is and listed afterwards.
          </p>
          {needsReason ? (
            <Field label="Reason (recorded on each order)">
              <input className="mk-fld" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
            </Field>
          ) : null}
          {error ? <Alert tone="stop">{error}</Alert> : null}
        </Modal>
      ) : null}

      {result ? (
        <Modal title="Status update result" onClose={() => setResult(null)} footer={<Button onClick={() => setResult(null)}>Close</Button>}>
          {result.succeeded.length > 0 ? (
            <Alert tone="done">
              {result.succeeded.length} {result.succeeded.length === 1 ? 'order' : 'orders'} moved to {LABEL[result.status as BulkOrderStatus] ?? result.status}:{' '}
              {result.succeeded.map((x) => x.reference).join(', ')}.
            </Alert>
          ) : null}
          {result.failed.length > 0 ? (
            <Alert tone="stop">
              <b>{result.failed.length} {result.failed.length === 1 ? 'order was' : 'orders were'} not changed:</b>
              <ul>
                {result.failed.map((x) => <li key={x.id}><b>{x.reference}</b> — {x.reason}</li>)}
              </ul>
            </Alert>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
